import React, { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import {
  GatewayDataType,
  GatewayVisibleSubsensors,
  getAllGatewayVisibleSubsensors,
  getAllowedGateways,
} from '../SensorConfig/helpers';
import { useAppSelector } from '../../state/store';
import { getCurrentLocation, getSensorsById, getSensorsByLocId } from '../../state/selectors';
import { themeProps } from '../../styles/theme';
import RSSILookup from '../SensorConfig/RSSILookup';

interface BaseSelectionProps {
  sensorId: string;
  selectedGatewayId: string;
  setGatewayId: (value: string) => void;
  visibleSubSensors?: GatewayVisibleSubsensors[];
}

function BaseSelection({
  sensorId,
  selectedGatewayId,
  setGatewayId,
  visibleSubSensors,
}: BaseSelectionProps): JSX.Element {
  const sensorsById = useAppSelector(getSensorsById);
  const sensorsByLocId = useAppSelector(getSensorsByLocId);
  const currentLocation = useAppSelector(getCurrentLocation);

  const [allowedGateways, setAllowedGateways] = useState<GatewayDataType[]>([]);

  const [gatewayVisibleSubsensors, setGatewayVisibleSubsensors] =
    useState<GatewayVisibleSubsensors[]>();

  useEffect(() => {
    if (visibleSubSensors) setGatewayVisibleSubsensors(visibleSubSensors);
    else {
      getAllGatewayVisibleSubsensors(currentLocation, sensorsById, sensorsByLocId).then(
        (subsensors) => setGatewayVisibleSubsensors(subsensors)
      );
    }
  }, [currentLocation, sensorsById, sensorsByLocId, visibleSubSensors]);

  // get all possible gateways for the sensor from that location
  useEffect(
    () => {
      setAllowedGateways([]);
      setGatewayId(''); // clear selected gateway

      if (gatewayVisibleSubsensors && sensorId) {
        getAllowedGateways(gatewayVisibleSubsensors, sensorId)
          .then((res) => {
            setAllowedGateways(res);
          })
          .catch(() => null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gatewayVisibleSubsensors, sensorId, currentLocation]
  );

  // set initial default gateway
  useEffect(() => {
    if (!selectedGatewayId && allowedGateways.length > 0) {
      setGatewayId(allowedGateways[0].gatewayId);
    }
  }, [allowedGateways, selectedGatewayId, setGatewayId]);

  return (
    <>
      <InputLabel
        id="gateway-id"
        style={{
          textAlign: 'left',
          fontSize: themeProps.textSize.small,
        }}
      >
        Selected Gateway*
      </InputLabel>
      <Select
        label="gateway"
        labelId="gateway-id"
        value={selectedGatewayId ?? ''}
        onChange={(e) => {
          const { value } = e.target;
          setGatewayId(value);
        }}
        variant="standard"
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '10px',
          height: '40px',
          marginBottom: '10px',
        }}
      >
        {allowedGateways.map((gateway) => (
          <MenuItem value={gateway.gatewayId} key={gateway.gatewayId}>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ alignSelf: 'center' }}>
                <RSSILookup RSSI={gateway.RSSI} />
              </Box>
              <Box sx={{ alignSelf: 'center', marginLeft: '5px' }}>
                {sensorsById.get(gateway.gatewayId)?.name ?? gateway.gatewayId}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </>
  );
}

BaseSelection.defaultProps = {
  visibleSubSensors: undefined,
};

export default BaseSelection;
